import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import ContactLines from '../../components/ContactLines';
import { FollowUpScheduler } from '../lead/LeadContacts';

// ─── Retention Kanban outcome modal (Sprint 21.2, 9/24/26) ───────────────────
// Per docs/COMS_Retention_Kanban_Design_Decisions_2026-09-24.md — each stage
// gets its own outcome vocabulary (not one shared Saved/Lost pair) and its
// own script cue. Posts through the same, already-live POST /retention/outcome
// route (logRetentionOutcome, queries.js) RetentionOutcomeModal.jsx already
// uses — this is a new frontend surface over existing/extended backend logic,
// not a new write path.

// Cancellation reasons, grouped by what the club can do about them (BK, 10/6/26). Staff still pick ONE
// specific primary reason; the bucket is for reporting and for choosing the right save offer.
const REASON_BUCKETS = [
  { label: 'Money', reasons: ['Price / cost'] },
  { label: 'Value and usage', reasons: ['Not enough time to use it', 'Lost interest / taking a break', 'No one to play with at my level'] },
  { label: 'Experience', reasons: ['Courts too busy / hard to book', 'Programming / class schedule', 'Customer service issue', 'Facility / cleanliness'] },
  { label: 'Life events (a pause may fit)', reasons: ['Health / injury', 'Weather / outdoor play (seasonal)'] },
  { label: 'Hard outs', reasons: ['Moving away', 'Switched to another club'] },
  { label: 'Other', reasons: ['Other'] },
];
const SAVE_OFFERS = ['None', 'Pause', 'Plan change', 'Discount', 'Program invite', 'Other'];

// Why an At Risk / Welld card is being taken off the list (the old picker offered cancellation
// reasons like "Price / cost", which make no sense here). The first one is the data-quality
// signal BK asked to count (10/5/26): a paying member who plays but isn't being checked in.
const ENGAGEMENT_REMOVE_REASONS = [
  'Plays regularly — visits not being recorded',
  'Visits under a family / other account',
  'Away / traveling (temporary)',
  'On pause or about to pause',
  'Not actually a member (data error)',
  'Staff / ambassador',
  'Other',
];

// "this is Jane at The Picklr McKinney" once Logged By is filled in; the club-only greeting until then.
const intro = (who, club) => `this is ${who && who.trim() ? `${who.trim()} at ` : ''}The Picklr${club ? ` ${club}` : ''}`;

const STAGE_CONFIG = {
  paymentFailed: {
    label: 'Payment Failed',
    icon: '💳',
    scriptKey: 'retention_payment_failed',
    script: (firstName, club, who) =>
      `Hi ${firstName}, ${intro(who, club)} — we tried to run your membership payment and it didn't go through. Can we get an updated card on file so there's no interruption to your access?`,
    outcomes: [
      { value: 'Re-engaged',      color: '#22c55e' },
      { value: 'Still failing',   color: '#f97316' },
      { value: 'No answer',       color: '#6b7280' },
      { value: 'Wants to cancel', color: '#ef4444', transitionsToCancellation: true },
    ],
    needsManagerPin: false,
    reasonOn: [],
  },
  cancellation: {
    label: 'Voluntary Cancellation',
    icon: '🚪',
    scriptKey: 'retention_cancellation_manager',
    script: (firstName, club, managerName) =>
      `Hi ${firstName}, this is ${managerName || '[Manager Name]'}, Manager at The Picklr ${club} — I saw you'd requested to cancel and wanted to reach out personally before it goes through. Is there anything we could have done differently, or anything we can still do to keep you playing with us?`,
    outcomes: [
      { value: 'Saved',             color: '#22c55e', needsReason: true },
      // A pause is not counted as a save: the member is still stepping away from billing for now.
      { value: 'Paused instead',    color: '#f59e0b', needsReason: true },
      { value: 'Confirmed leaving', color: '#ef4444', needsReason: true, needsDetail: true },
      { value: 'No answer',         color: '#6b7280' },
    ],
    needsManagerPin: true,
    reasonOn: ['Saved', 'Paused instead', 'Confirmed leaving'],
  },
  atRisk: {
    label: 'Disengaging / At-Risk',
    icon: '📉',
    outcomes: [
      { value: 'Booked into program',       color: '#22c55e' },
      { value: 'Re-engaged',                color: '#22c55e' },
      { value: 'No answer',                 color: '#6b7280' },
      { value: 'Remove from retention list', color: '#f97316', needsReason: true },
    ],
    needsManagerPin: false,
    reasonOn: [],
  },
  // Sprint 21.3, 10/1/26 — the missing piece that finally lets an escalated
  // case reach a terminal state from this board (was read-only, no click,
  // no drag — see logInsideSalesRetentionOutcome's own design comment,
  // queries.js, for why it was deliberately kept this minimal: Save/No Save
  // + notes, no cancellation-reason categorization yet). Posts to a
  // different route than every other stage here — Josh isn't a club login,
  // so there's no manager PIN and no issueType to infer from stageKey
  // (Escalated merges both failed_payment and voluntary_cancellation —
  // issueType comes from the card itself).
  escalated: {
    label: 'Escalated',
    icon: '📤',
    // Attempts (no answer / voicemail / still deciding) log a contact and keep the case open so a
    // follow-up can be scheduled; only Saved and No Save end it.
    outcomes: [
      { value: 'No answer',      color: '#6b7280' },
      { value: 'Left voicemail', color: '#6b7280' },
      { value: 'Still deciding', color: '#f59e0b' },
      { value: 'Saved',          color: '#22c55e' },
      { value: 'No Save',        color: '#ef4444' },
    ],
    needsManagerPin: false,
    reasonOn: [],
    insideSales: true,
  },
};

// Welld Engagement (BK, 10/5/26): Welld members who haven't visited in 14+ days get
// their own stage (and their own owner/metrics), taken out of At Risk. Same outcomes
// and the same contact logging (queue 'at_risk') as At Risk.
STAGE_CONFIG.welld = { ...STAGE_CONFIG.atRisk, label: 'Welld Engagement', icon: '💙' };

// Escalated = Inside Sales' last shot after the club/manager window closed (BK,
// 10/5/26: "one last shot to save"). Drafts for Josh to edit; keep offers (pause, lower-cost
// plan) to whatever policy allows.
const ESCALATED_SCRIPTS = {
  failed_payment: {
    scriptKey: 'retention_escalated_payment',
    script: (firstName, club, who) =>
      `Hi ${firstName}, it's ${who || '[Your name]'} with The Picklr${club ? ` ${club}` : ''}. I'm following up personally because your membership payment didn't go through and we haven't been able to get it sorted. I don't want you to lose your access, so I can help you update your card or take payment right now. Can we take care of it today? And if something else is going on, I'd genuinely like to hear it.`,
  },
  voluntary_cancellation: {
    scriptKey: 'retention_escalated_cancellation',
    script: (firstName, club, who) =>
      `Hi ${firstName}, it's ${who || '[Your name]'} with The Picklr${club ? ` ${club}` : ''}. I heard you're planning to cancel, and I wanted to reach out personally. I'm not here to talk you out of anything, but I want to be sure we've done everything we can. What's the main reason?\n\n[Listen. If cost: a pause or a lower-cost plan, if available. If schedule or programming: offer a specific session that fits. If moving or travelling: wish them well and ask about a pause.]\n\nIs there anything that would make you want to stay?`,
  },
};

const FLAG_SCRIPTS = {
  range: {
    scriptKey: 'retention_atrisk_range',
    script: (firstName, club, programName, who) =>
      `Hey ${firstName}, ${intro(who, club)} — I noticed you've mostly been coming for Open Play. Have you tried ${programName || 'one of our programs'}? I think you'd really enjoy it, want me to get you booked in this week?`,
  },
  people: {
    scriptKey: 'retention_atrisk_people',
    script: (firstName, club, who) =>
      `Hey ${firstName}, ${intro(who, club)} — I wanted to introduce you to a few regulars who play around your level and schedule. Want me to connect you with [Name] for your next visit?`,
  },
  rhythm: {
    scriptKey: 'retention_atrisk_rhythm',
    script: (firstName, club, who) =>
      `Hey ${firstName}, ${intro(who, club)} — haven't seen you on the court in a bit. Want me to set you up with a regular weekly spot? [Day/Time] tends to have a great group.`,
  },
};

// Every At Risk card is now defined by days inactive (14+), not by a flag, so most
// cards have none of Range/People/Rhythm — and used to get NO script at all
// (BK, 10/5/26: "there are no scripts in the Log outcome"). These two apply to any
// At Risk card; the flag scripts stay, offered first when a card has that flag.
// Wording is a first draft for the club team to edit — keep the "check in, offer to
// book" shape and avoid promising anything about plan benefits.
const GENERAL_SCRIPTS = {
  general: {
    label: 'General',
    scriptKey: 'retention_atrisk_general',
    script: (firstName, club, days, who) =>
      `Hey ${firstName}, ${intro(who, club)} — I noticed we haven't seen you in ${days ? `${days} days` : 'a while'} and wanted to check in. Is everything okay? Is there anything we can do to help get you back on the court this week?`,
  },
  welld: {
    label: 'Welld',
    scriptKey: 'retention_atrisk_welld',
    script: (firstName, club, days, plan, who) =>
      `Hey ${firstName}, ${intro(who, club)} — we haven't seen you in ${days ? `${days} days` : 'a while'} and wanted to check in. Your ${plan || 'membership'} is all set to use whenever you're ready — want me to help you pick a time to come in this week?`,
  },
};

// Range leads (design doc: "biggest lever, drags the other two down with
// it") whenever a card has more than one flag.
const FLAG_PRIORITY = ['range', 'people', 'rhythm'];

const LOGGED_BY_KEY = 'coms_retention_logged_by';

function ScriptBox({ text, scriptKey, onCopy }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Script
      </div>
      <div style={{
        background: '#111', border: '1px solid #333', borderRadius: 5,
        padding: '8px 9px', fontSize: 12, color: '#ccc', lineHeight: 1.45, whiteSpace: 'pre-wrap',
      }}>
        {text}
      </div>
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 6, fontSize: 11 }}
        onClick={() => {
          navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            onCopy?.(scriptKey);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? '✓ Copied' : '📋 Copy script'}
      </button>
    </div>
  );
}

// Outcomes that end the contact — no follow-up to schedule.
const NO_FOLLOWUP_OUTCOMES = ['Saved', 'Confirmed leaving', 'No Save', 'Re-engaged', 'Booked into program',
  'Remove from retention list', 'Wants to cancel'];

export default function RetentionKanbanOutcomeModal({ card, stageKey, clubName, onClose, onSaved }) {
  const { post, get } = useApi();
  const { currentClub } = useClub();

  const config = STAGE_CONFIG[stageKey];
  const firstName = card.name?.split(' ')[0] || 'there';

  const [usedScriptKey, setUsedScriptKey] = useState(null);
  const { user } = useAuth();
  // Inside Sales (Escalated) logs in as themselves, so their own name fills the script and the
  // "Logged by" box. Club logins are shared, so those still type a name each time.
  const ownName = (user?.name && !String(user.name).startsWith('auth0|')) ? user.name : '';
  const [contactedByName, setContactedByName] = useState(() => {
    try { return sessionStorage.getItem(LOGGED_BY_KEY) || (STAGE_CONFIG[stageKey]?.insideSales ? ownName : ''); } catch { return ''; }
  });
  const [managerPin, setManagerPin] = useState('');
  const [contactMethods, setContactMethods] = useState([]);
  const [outcome, setOutcome] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveOffer, setSaveOffer] = useState('');
  const [offerAccepted, setOfferAccepted] = useState(null);
  const [switchedType, setSwitchedType] = useState('');
  const [switchedName, setSwitchedName] = useState('');
  const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
  const [followUp, setFollowUp] = useState({ date: '', time: '9:00 AM', note: '' });

  // At-Risk: which flag's script to lead with (BK: Range first when both present).
  // Cancellation's script reactively shows whatever's typed into Logged By so
  // far — a manager fills that in before/while reading the script, since it's
  // the caller's own name in a phone greeting (see script copy below).
  // At Risk: every card gets a script. Order: Welld (if a Welld member), then any
  // flag scripts in priority order (Range first), then General.
  const isEngagement = stageKey === 'atRisk' || stageKey === 'welld';
  const atRiskScripts = isEngagement ? [
    ...(card.isWelld ? [{ label: GENERAL_SCRIPTS.welld.label, key: GENERAL_SCRIPTS.welld.scriptKey,
        text: GENERAL_SCRIPTS.welld.script(firstName, clubName, card.daysSinceLastVisit, card.membershipPlan, contactedByName) }] : []),
    ...FLAG_PRIORITY.filter(f => (card.flags || []).includes(f)).map(f => ({
      label: f.charAt(0).toUpperCase() + f.slice(1), key: FLAG_SCRIPTS[f].scriptKey,
      text: f === 'range' ? FLAG_SCRIPTS[f].script(firstName, clubName, card.lastProgramType, contactedByName)
        : FLAG_SCRIPTS[f].script(firstName, clubName, contactedByName),
    })),
    { label: GENERAL_SCRIPTS.general.label, key: GENERAL_SCRIPTS.general.scriptKey,
      text: GENERAL_SCRIPTS.general.script(firstName, clubName, card.daysSinceLastVisit, contactedByName) },
  ] : [];
  const [scriptTab, setScriptTab] = useState(0);
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!isEngagement || !card.memberId || !currentClub?.id) return;
    let live = true;
    get(`/retention/member-profile?locationId=${currentClub.id}&memberId=${encodeURIComponent(card.memberId)}`)
      .then(p => { if (live) setProfile(p); })
      .catch(() => { if (live) setProfile(false); }); // panel is a nice-to-have; never blocks logging
    return () => { live = false; };
  }, [card.memberId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickedAtRisk = atRiskScripts[Math.min(scriptTab, Math.max(atRiskScripts.length - 1, 0))] || null;
  const escScript = stageKey === 'escalated' ? ESCALATED_SCRIPTS[card.issueType] : null;
  const scriptKey = isEngagement ? pickedAtRisk?.key : (escScript ? escScript.scriptKey : config.scriptKey);
  const scriptText = isEngagement
    ? (pickedAtRisk ? pickedAtRisk.text : null)
    : escScript ? escScript.script(firstName, clubName, contactedByName)
    : !config.script ? null
      : (stageKey === 'cancellation'
          ? config.script(firstName, clubName, contactedByName)
          : config.script(firstName, clubName, contactedByName));

  const outcomeObj = config.outcomes.find(o => o.value === outcome) || null;
  const isCancellationCase = stageKey === 'cancellation' || (config.insideSales && card.issueType === 'voluntary_cancellation');
  const needsReason = outcomeObj?.needsReason
    || (config.insideSales && card.issueType === 'voluntary_cancellation' && ['Saved', 'No Save'].includes(outcome))
    || false;
  const showOfferBlock = isCancellationCase && needsReason;
  const isSwitched = reason === 'Switched to another club';
  const needsDetail = outcomeObj?.needsDetail || false;

  function handleNameChange(v) {
    setContactedByName(v);
    try { sessionStorage.setItem(LOGGED_BY_KEY, v); } catch { /* ignore */ }
  }
  function toggleContactMethod(m) {
    setContactMethods(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  }

  async function handleSave() {
    if (!outcome) { setError('Select an outcome'); return; }
    if (!contactedByName.trim()) { setError('Enter your name — the club login is shared, we need to know who logged this'); return; }
    if (config.needsManagerPin && !managerPin.trim()) { setError('Manager PIN is required to log a cancellation contact'); return; }
    if (needsReason && !reason) { setError('Select a reason'); return; }
    if (isSwitched && !switchedType) { setError('Say whether they switched to another Picklr club or an outside facility'); return; }
    if (showOfferBlock && outcome !== 'Paused instead' && !saveOffer) { setError('Record what was offered to keep them (choose None if nothing)'); return; }
    if (needsDetail && !notes.trim()) {
      setError('A detailed explanation is required for "Confirmed leaving" — what did they actually say? A reason code alone isn\'t enough.');
      return;
    }

    const offerUsed = outcome === 'Paused instead' ? 'Pause' : (showOfferBlock ? saveOffer : '');
    const offerFields = showOfferBlock ? {
      saveOffer: offerUsed || undefined,
      saveOfferAccepted: outcome === 'Paused instead' ? true : (offerUsed && offerUsed !== 'None' ? offerAccepted === true : undefined),
      switchedTo: isSwitched ? `${switchedType}${switchedName.trim() ? `: ${switchedName.trim()}` : ''}` : undefined,
      cancellationReason: reason || undefined,
    } : {};
    const wantsFollowUp = scheduleFollowUp && !NO_FOLLOWUP_OUTCOMES.includes(outcome);
    if (wantsFollowUp && !followUp.date) { setError('Pick a follow-up date, or turn the follow-up off'); return; }
    const followupDate = wantsFollowUp ? followUp.date : undefined;
    const followupTime = wantsFollowUp ? followUp.time : undefined;
    // retention_actions has no follow-up note column, so the note rides along in the contact notes.
    const savedNotes = wantsFollowUp && followUp.note?.trim()
      ? `${notes}${notes ? '\n' : ''}Follow-up note: ${followUp.note.trim()}` : notes;

    setSaving(true);
    setError(null);

    // Escalated is Inside Sales' case, not the club's — different route,
    // different payload shape (no manager PIN, no club-side issueType
    // inference; the card already carries its own real issueType since
    // Escalated merges both failed_payment and voluntary_cancellation).
    if (config.insideSales) {
      try {
        await post('/lead/daily-worklist/retention-outcome', {
          memberId: card.memberId,
          locationId: currentClub?.id,
          issueType: card.issueType,
          contactMethods,
          outcome,
          notes: savedNotes,
          followupDate,
          followupTime,
          contactedByName: contactedByName.trim(),
          isSave: outcome === 'Saved',
          ...offerFields,
        });
        onSaved(outcome);
      } catch (e) {
        setError(e.message || 'Failed to save');
      } finally {
        setSaving(false);
      }
      return;
    }

    // issueType for the backend: cardMap → API vocabulary already used by
    // logRetentionOutcome ('failed_payment' / 'voluntary_cancellation' /
    // 'at_risk').
    const issueType = stageKey === 'paymentFailed' ? 'failed_payment'
      : stageKey === 'cancellation' ? 'voluntary_cancellation'
      : 'at_risk';

    try {
      await post('/retention/outcome', {
        memberId: card.memberId,
        locationId: currentClub?.id,
        issueType,
        contactMethods,
        outcome,
        notes: savedNotes,
        followupDate,
        followupTime,
        contactedByName: contactedByName.trim(),
        managerPin: config.needsManagerPin ? managerPin : undefined,
        isSave: outcome === 'Saved',
        cancellationReason: needsReason ? reason : undefined,
        cancellationDate: (stageKey === 'cancellation' && outcome === 'Confirmed leaving') ? new Date().toISOString().slice(0, 10) : undefined,
        voluntary: stageKey === 'cancellation' ? true : undefined,
        wantsToCancel: outcomeObj?.transitionsToCancellation === true,
        ...offerFields,
        scriptKey: usedScriptKey || scriptKey || undefined,
      });
      onSaved(outcome);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>

        <div className="modal-title">
          {config.icon} Log Contact — <span style={{ color: '#facc15' }}>{card.name}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#666', marginBottom: 14 }}>
          <span>{config.label}</span>
          {card.phone && <span>{card.phone}</span>}
        </div>

        {isEngagement && (
          <div style={{ background: '#0f1a14', border: '1px solid #1f3a2a', borderRadius: 6, padding: '9px 10px', marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Prep — how and when they play <span style={{ color: '#667', textTransform: 'none', letterSpacing: 0 }}>(a soft guide, not a script)</span>
            </div>
            <ContactLines phone={card.phone} email={card.email} name={card.name} size="sm" />
            {profile === null && <div style={{ fontSize: 11, color: '#667' }}>Loading their play pattern…</div>}
            {profile === false && <div style={{ fontSize: 11, color: '#667' }}>Couldn't load their play pattern — look them up in PlayByPoint.</div>}
            {profile && (
              <div style={{ fontSize: 11.5, color: '#c9d3cc', lineHeight: 1.5 }}>
                {(profile.summaryLines || []).map((l, i) => <div key={i}>• {l}</div>)}
                <div style={{ color: '#8aa092', marginTop: 4 }}>
                  Last 90 days: {profile.visitDays90} visit day{profile.visitDays90 === 1 ? '' : 's'}
                  {' · '}Before that: {profile.visitDaysPrior90}
                  {card.membershipPlan ? <> · Plan: {card.membershipPlan}</> : null}
                  {' · '}{profile.duprRating ? `DUPR ${profile.duprRating}` : profile.skillLevel ? `Skill: ${profile.skillLevel}` : 'Level: not on file'}
                </div>
                {(profile.lastVisits || []).length > 0 && (
                  <div style={{ color: '#8aa092', marginTop: 2 }}>
                    Recent: {profile.lastVisits.map(v => `${new Date(v.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}${v.time ? ' ' + v.time : ''} ${v.type}`).join(' · ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#8aa092', marginTop: 7, lineHeight: 1.5, borderTop: '1px solid #1f3a2a', paddingTop: 6 }}>
              <strong style={{ color: '#a8c7b3' }}>Approach:</strong> 1) Look them up in PlayByPoint (the link above) and see when, how and at what level they play.
              2) Check the schedule for an opening at their usual time and level. 3) Suggest that specific session and invite them to sign up.
              The script below is a starting point — your own read of the person beats it.
            </div>
          </div>
        )}

        {isEngagement && (card.flags || []).length > 0 && (
          <div style={{ marginBottom: 10, fontSize: 11, color: '#888' }}>
            Flags: {(card.flags || []).map(f => f === 'range' ? 'Range' : f === 'people' ? 'People' : f).join(', ')}
          </div>
        )}

        {isEngagement && atRiskScripts.length > 1 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
            {atRiskScripts.map((sc, i) => (
              <span key={sc.key} onClick={() => { setScriptTab(i); setUsedScriptKey(null); }}
                style={{ fontSize: 10.5, padding: '3px 9px', borderRadius: 20, cursor: 'pointer',
                  border: `1px solid ${i === scriptTab ? '#f59e0b' : '#2a2a2a'}`,
                  background: i === scriptTab ? '#f59e0b22' : '#141414', color: i === scriptTab ? '#f59e0b' : '#aaa' }}>
                {sc.label}
              </span>
            ))}
          </div>
        )}
        {scriptText && (
          <ScriptBox text={scriptText} scriptKey={scriptKey} onCopy={setUsedScriptKey} />
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Logged By * <span style={{ fontWeight: 400, textTransform: 'none', color: '#666' }}>(club login is shared — this is how we'd follow up with the right person)</span>
          </div>
          <input
            type="text"
            value={contactedByName}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="Your name"
            style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12, boxSizing: 'border-box' }}
          />
        </div>

        {config.needsManagerPin && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Manager PIN * <span style={{ fontWeight: 400, textTransform: 'none', color: '#666' }}>(required — every cancellation is manager-owned)</span>
            </div>
            <input
              type="password"
              value={managerPin}
              onChange={e => setManagerPin(e.target.value)}
              placeholder="Enter manager PIN"
              maxLength={6}
              style={{ maxWidth: 140, letterSpacing: 2, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12, boxSizing: 'border-box' }}
            />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Contact Method
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { value: 'call', label: '📞 Call' },
              { value: 'text', label: '💬 Text' },
              { value: 'email', label: '📧 Email' },
              { value: 'in-person', label: '🤝 In Person' },
            ].map(cm => {
              const active = contactMethods.includes(cm.value);
              return (
                <button
                  key={cm.value}
                  onClick={() => toggleContactMethod(cm.value)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? '#facc15' : '#333'}`,
                    background: active ? '#facc1515' : '#111',
                    color: active ? '#facc15' : '#666',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cm.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Outcome *
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {config.outcomes.map(o => {
              const active = outcome === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => { setOutcome(o.value); setReason(''); }}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? o.color : '#333'}`,
                    background: active ? `${o.color}15` : '#111',
                    color: active ? o.color : '#777',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {o.value}
                </button>
              );
            })}
          </div>
          {outcomeObj?.transitionsToCancellation && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#f97316', fontStyle: 'italic' }}>
              This moves the card into Voluntary Cancellation — Manager ownership, and a fresh 7-day window starts now.
            </div>
          )}
        </div>

        {needsReason && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {isCancellationCase ? 'Main reason (required — pick the one that matters most)' : 'Reason (required)'}
            </div>
            {(isCancellationCase ? REASON_BUCKETS : [{ label: null, reasons: ENGAGEMENT_REMOVE_REASONS }]).map(b => (
              <div key={b.label || 'all'} style={{ marginBottom: 6 }}>
                {b.label && <div style={{ fontSize: 9.5, color: '#666', marginBottom: 3 }}>{b.label}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {b.reasons.map(r => (
                    <button
                      key={r}
                      onClick={() => setReason(r)}
                      style={{
                        fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${reason === r ? '#ef4444' : '#333'}`,
                        background: reason === r ? '#ef444415' : '#111',
                        color: reason === r ? '#ef4444' : '#777',
                        fontWeight: reason === r ? 700 : 400,
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {isSwitched && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Where did they go? (required)</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {['Another Picklr club', 'Outside facility'].map(t => (
                <button key={t} onClick={() => setSwitchedType(t)} style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${switchedType === t ? '#f59e0b' : '#333'}`, background: switchedType === t ? '#f59e0b15' : '#111',
                  color: switchedType === t ? '#f59e0b' : '#777', fontWeight: switchedType === t ? 700 : 400 }}>{t}</button>
              ))}
            </div>
            <input type="text" value={switchedName} onChange={e => setSwitchedName(e.target.value)}
              placeholder={switchedType === 'Another Picklr club' ? 'Which club?' : 'Which facility? (name, if they said)'}
              style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12, boxSizing: 'border-box' }} />
          </div>
        )}

        {showOfferBlock && outcome !== 'Paused instead' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              What did you offer to keep them? (required)
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SAVE_OFFERS.map(o => (
                <button key={o} onClick={() => { setSaveOffer(o); if (o === 'None') setOfferAccepted(null); }} style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${saveOffer === o ? '#facc15' : '#333'}`, background: saveOffer === o ? '#facc1515' : '#111',
                  color: saveOffer === o ? '#facc15' : '#777', fontWeight: saveOffer === o ? 700 : 400 }}>{o}</button>
              ))}
            </div>
            {saveOffer && saveOffer !== 'None' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: '#aaa' }}>
                Did they take it?
                {[['Yes', true], ['No', false]].map(([label, val]) => (
                  <button key={label} onClick={() => setOfferAccepted(val)} style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${offerAccepted === val ? '#22c55e' : '#333'}`, background: offerAccepted === val ? '#22c55e15' : '#111',
                    color: offerAccepted === val ? '#22c55e' : '#777' }}>{label}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {outcome === 'Paused instead' && (
          <div style={{ marginBottom: 14, fontSize: 11, color: '#f59e0b', fontStyle: 'italic' }}>
            Logged as a pause offered and accepted. It is not counted as a save. Schedule a follow-up for when the pause ends.
          </div>
        )}

        {outcome && !NO_FOLLOWUP_OUTCOMES.includes(outcome) && (
          <div style={{ marginBottom: 14 }}>
            <div onClick={() => setScheduleFollowUp(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 20, borderRadius: 10, background: scheduleFollowUp ? '#facc15' : '#333', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: scheduleFollowUp ? '#000' : '#666', position: 'absolute', top: 2, left: scheduleFollowUp ? 18 : 2, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, color: scheduleFollowUp ? '#facc15' : '#888' }}>Schedule a follow-up</span>
            </div>
            {scheduleFollowUp && (
              <div style={{ background: '#0d1117', border: '1px solid #facc1530', borderRadius: 8, padding: '12px 14px' }}>
                <FollowUpScheduler value={followUp} onChange={setFollowUp} />
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: needsDetail ? '#ef4444' : '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Notes {needsDetail ? '* (required for Confirmed leaving)' : '(optional)'}
          </div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={needsDetail ? 'What did they actually say? This is what theme reports get built from.' : 'Additional context...'}
            style={{
              width: '100%', background: '#111',
              border: `1px solid ${needsDetail ? '#ef444466' : '#333'}`,
              color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12,
              resize: 'none', height: 48, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          {needsDetail && !notes.trim() && (
            <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
              A detailed explanation is required — a reason code alone won't tell us anything new.
            </div>
          )}
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !outcome}>
            {saving ? 'Saving...' : '✓ Save Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
